import type { ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import { type SelectionViewModel } from '@/features/iam/users/SelectionViewModel'
import { InfiniteScrollLoaderContainer } from '@/components/InfiniteScrollLoader/InfiniteScrollLoaderContainer'
import {
  SelectionList,
  SelectionListItem,
} from '@/features/iam/users/components/SelectionList'

interface CommonProps<T> {
  editing?: boolean
  selectionVm: SelectionViewModel<T>
  icon: ReactNode

  getContent(item: T): {
    title: string
    description: string
  }
}

export interface SelectionListContainerProps<T> extends CommonProps<T> {}

export const SelectionListContainer = observer(function SelectionListContainer<
  T,
>({ selectionVm, getContent, editing, icon }: SelectionListContainerProps<T>) {
  const items = editing ? selectionVm.filteredItems : selectionVm.selectedItems

  return (
    <SelectionList
      editing={editing}
      onSearchTextChange={selectionVm.setSearchText}
      fetchStatus={selectionVm.scrollProvider.fetch.state}
      fetchError={selectionVm.scrollProvider.fetch.error}
      onTryAgain={selectionVm.fetchItems}
      isEmpty={items.length === 0}
      loader={
        !selectionVm.scrollProvider.isDone && (
          <InfiniteScrollLoaderContainer
            scrollProvider={selectionVm.scrollProvider}
          />
        )
      }
    >
      {items.map((item, i) => (
        <SelectionListItemContainer
          isFirstItem={i === 0}
          key={selectionVm.getItemId(item)}
          item={item}
          getContent={getContent}
          icon={icon}
          selectionVm={selectionVm}
          editing={editing}
        />
      ))}
    </SelectionList>
  )
})

export interface SelectionListItemContainerProps<T> extends CommonProps<T> {
  item: T
  isFirstItem: boolean
}

export const SelectionListItemContainer = observer(
  function SelectionListItemContainer<T>({
    item,
    getContent,
    icon,
    editing,
    isFirstItem,
    selectionVm,
  }: SelectionListItemContainerProps<T>) {
    const content = getContent(item)
    return (
      <SelectionListItem
        title={content.title}
        description={content.description}
        icon={icon}
        editing={editing}
        isFirstItem={isFirstItem}
        canUpdate={selectionVm.canUpdate(item)}
        checked={selectionVm.isSelected(item)}
        isTogglePending={selectionVm.isTogglePending(item)}
        onToggle={() => selectionVm.toggle(item)}
      />
    )
  },
)
